/**
 * People reachable through the organisation's partnerships.
 *
 * Three keys, three cache profiles (registered in query-defaults.ts):
 * `network-people` is LIST_LIVE so cached rows paint instantly and still
 * revalidate on every mount; `network-person` is DETAIL; and
 * `network-people-counts` is COUNTS, because the pill numbers are a
 * whole-tenant aggregate that must not refire on every mount or window focus.
 *
 * No staleTime/refetch options are set here on purpose - the profiles own that.
 *
 * ``r.data`` is the payload, never ``r.data.result``: the admin api client's
 * response interceptor already strips the ``{status, message, result}`` envelope
 * (see shared/lib/api-envelope.ts), so reaching for ``.result`` yields undefined.
 * The list endpoint is unaffected either way because DRF pagination is not
 * enveloped, which is what made this look like two unrelated bugs.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'

/** How (or whether) a person reaches one office. Mirrors AccessState. */
export type OfficeAccess =
  | 'direct_grant'
  | 'inherited'
  | 'no_grant'
  | 'orphaned_grant'
  /** The partnership itself is suspended, so nothing it carries lets them in. */
  | 'suspended'

/**
 * One office a person can work at, after the head-office collapse: a granted
 * head office already covers every location under it, so those locations are
 * NOT separate rows - the head office carries the counts instead.
 */
export interface PartnerOffice {
  partnership_uuid: string
  organisation_uuid: string
  name: string
  /** Null when this office is a head office or a standalone partner. */
  parent_name: string | null
  parent_organisation_uuid: string | null
  /** The head office's own partnership, so the breadcrumb can link to it. */
  parent_partnership_uuid: string | null
  role_name: string | null
  access: OfficeAccess
  is_head_office: boolean
  is_active: boolean
  granted_at: string | null
  member_since: string | null
  /**
   * The membership row behind `member_since`. Removal is keyed on the
   * membership, not the organisation, so without this the UI cannot call the
   * endpoint at all. Null when the tie is a grant with no membership.
   */
  membership_uuid: string | null
  /** Locations this head office covers. 0 on a location or standalone partner. */
  covered_location_count: number
  /** Covered locations that redundantly hold their own grant. */
  redundant_grant_count: number
}

export interface NetworkPerson {
  uuid: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  last_login: string | null
  is_active: boolean
  offices: PartnerOffice[]
  office_count: number
  /** Distinct partner businesses, so a group counts once however many offices. */
  business_count: number
}

export interface NetworkPersonDetail extends NetworkPerson {
  date_joined: string | null
  activity: {
    registrations: number
    claims: number
    last_registration_at: string | null
  }
  invitation: {
    uuid: string
    email: string
    organisation_name: string
    created_at: string
    sent_at: string | null
    accepted_at: string | null
    cancelled_at: string | null
    reactivation_count: number
    status: 'accepted' | 'cancelled' | 'expired' | 'pending'
  } | null
}

/**
 * Counts for the access filter pills.
 *
 * NOT a partition: one person can hold offices in several states at once
 * (granted at one location, stranded at another), so these do not sum to `all`.
 */
export interface NetworkPeopleCounts {
  all: number
  direct_grant: number
  inherited: number
  no_grant: number
  orphaned_grant: number
  suspended: number
}

export interface NetworkPeopleResponse {
  count: number
  results: NetworkPerson[]
}

export interface NetworkPeopleParams {
  page?: number
  page_size?: number
  search?: string
  ordering?: string
  /** One of OfficeAccess; omitted means every state. */
  access?: string
}

export function useNetworkPeople(params: NetworkPeopleParams) {
  const { orgUuid } = useOrg()
  return useQuery<NetworkPeopleResponse>({
    queryKey: ['network-people', orgUuid, params],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partner-people/`, { params })
        .then((r) => r.data),
    enabled: !!orgUuid,
    // Keep the current page painted while the next page/filter loads, so rows
    // do not flash empty on navigation.
    placeholderData: keepPreviousData,
  })
}

export function useNetworkPeopleCounts() {
  const { orgUuid } = useOrg()
  return useQuery<NetworkPeopleCounts>({
    queryKey: ['network-people-counts', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partner-people/status-counts/`)
        .then((r) => r.data),
    enabled: !!orgUuid,
  })
}

export function useNetworkPerson(userUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<NetworkPersonDetail>({
    queryKey: ['network-person', orgUuid, userUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partner-people/${userUuid}/`)
        .then((r) => r.data),
    enabled: !!orgUuid && !!userUuid,
  })
}

/**
 * Remove a person from ONE office.
 *
 * Soft-deletes that office's membership and, server-side, revokes the
 * `PartnershipUserAccess` it granted - the row that actually puts them in the
 * dealer's partner context. Scoped to the one office, so a person moved off a
 * site keeps every other office they hold.
 *
 * The server refuses the org owner and self-removal (both orphan the access
 * needed to undo it), surfaced here as the API's own message.
 */
export function useRemovePersonFromOffice() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      organisationUuid,
      membershipUuid,
    }: {
      organisationUuid: string
      membershipUuid: string
    }) =>
      api.delete(`/organisations/${organisationUuid}/members/${membershipUuid}/`),
    onSuccess: () => {
      // The person list, its filter counts and the person page all describe
      // the same tie, so all three go stale together.
      queryClient.invalidateQueries({ queryKey: ['network-people', orgUuid] })
      queryClient.invalidateQueries({
        queryKey: ['network-people-counts', orgUuid],
      })
      queryClient.invalidateQueries({ queryKey: ['network-person', orgUuid] })
    },
  })
}
